/**
 * Menu configurations for the Conference Room Designer.
 * This module defines the structure and properties of menus for different views in the application.
 * Since most menus are very similar to modals, rather than redefine the same block, this configuration
 * uses blocks already defined as much as possible
 * @module conferenceRoomDesignerMenus
 */
import forms from "toolConstants/conferenceroomDesigner/forms";
import constants from "toolConstants/conferenceroomDesigner/constants";
import { isDevioUcxDesign, UCX_UNSUPPORTED_UC_COMPUTE } from "utilities/treeFrogBundleMatching";

/**
 * Menu configurations for various views in the Conference Room Designer.
 * @type {Object.<string, Object>}
 * 
 * @property {Object} floor_plan - Floor plan view menu configuration.
 * @property {Object} ceiling - Ceiling view menu configuration.
 * @property {Object} furnishing - Furnishing view menu configuration.
 * @property {Object} acoustics - Acoustics view menu configuration.
 * @property {Object} microphones - Microphones view menu configuration.
 * @property {Object} speakers - Speakers view menu configuration.
 * @property {Object} camera - Camera view menu configuration.
 * @property {Object} equipment_list - Equipment list view menu configuration.
 * 
 * @description
 * Each menu item typically includes:
 * - element: Defines the component used to render the menu item (mapped from componentIndex).
 * - name: Usually the key used when dispatching the variable to the Redux store.
 * - dispatcher: Points to the dispatch function used to update the store.
 * - condition: An object that defines when the menu item should be displayed or enabled.
 *   The condition object is passed to the getConditionResult function, which evaluates
 *   whether the condition is met based on the current state. The condition object typically
 *   includes:
 *   - parameter: An array of state properties to check.
 *   - check: A function that receives the specified state properties and returns a boolean.
 * The getConditionResult function (documented in container.js) is used to evaluate these conditions
 * and determine whether each menu item should be displayed or enabled based on the current state.
 * 
 * Additional properties may include condition, label, range, theme, bubbles, etc.
 * 

 */

  const { CEILING_MOUNTED, HAS_CAMERA, BAR, PENDANT, CBC, CEILING_TILE } = constants.equipment.micCategories;
  // isBarMic handles both migrated keys (e.g. 'vbc_2500a') and legacy 'bar' key
  const isBarMic = (model) => model === 'bar' || BAR.has(model);
  const isCeilingTileMic = (model) => CEILING_TILE.has(model);
  // hasCamera handles migrated keys and legacy 'bar' key (use modelName as tiebreaker for legacy)
  const hasCamera = (model, modelName) => HAS_CAMERA.has(model) || (model === 'bar' && modelName && (modelName.includes('VBC')));

const menus = {
  floor_plan: {
    area: [...forms.area.body.slice(1, -2)],
  },
  ceiling: {
    area: [
      forms.area.body[3],
      {
        condition: { parameter: ['ceilingTiles'], check: ([tiles]) => tiles.tile !== 'no_ceiling_tiles' },
        element: 'doubleNumberInput',
        label: 'Number of tiles',
        range: { min: 1 },
        ids: ['qty.x', 'qty.y'],
        dispatcher: 'setCeilingTiles',
      }
    ],
  },
  furnishing: {
    area: [
      { ...forms.table.body[0], theme: 'furnitureGroupNav' },
      ...forms.table.body.slice(1, 3),
    ],
  },
  acoustics: {
    area: [
      {
        element: 'toggleButtons',
        items: constants.acoustics, 
        labels: true,
        theme: 'acousticGroupNav',
        activeState: 'acoustic_nav_active',
        inactiveState: 'acoustic_nav_inactive',
        dispatcher: 'setAcoustics',
        infoLink: "https://support.biamp.com/Tesira/Parle_microphones/Parl%C3%A9_ceiling_microphone_calculator/Sample_recordings",
        infoText: 'What do these settings mean?',
        extend: true,
      }
    ],
  },
  microphones: {
    area: [
      {
        element: 'selector',
        label: 'Type',
        name: 'model',
        // PIG-465: ordered alphabetically by display label (Bar, Ceiling, Ceiling Tile, Pendant, Table)
        items: ['vbc_2500a', 'tcm_x', 'bma360d_24', 'tcm_1', 'ttm_x'],
        dispatcher: 'setMicrophones',
        valueTransform: (model) => {
          if (BAR.has(model)) return 'vbc_2500a';
          if (CEILING_TILE.has(model)) return 'bma360d_24';
          return model;
        },
        filter: {
          parameter: 'table',
          filter: (table, items) => {
            // If no table shape is defined, filter out tabletop mic
            if (!table || !table.shape) {
              return items.filter(item => item !== 'ttm_x');
            }
            // Otherwise return all items
            return items;
          }
        },
        bubbles: {
          tcm_x: 'Ceiling',
          tcm_1: 'Pendant',
          ttm_x: 'Table',
          vbc_2500a: 'Bar',
          bma360d_24: 'Ceiling Tile'
        }
      },
      {
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            return microphones && isBarMic(microphones.model);
          }
        },
        element: 'selector',
        label: 'Model',
        name: 'modelName',
        // PIG-465: ordered alphabetically
        items: ['Parlé ABC 2500a', 'Parlé CBC 2500 CM', 'Parlé CBC 2500 PM', 'Parlé VBC 2500a', 'Parlé VBC 2800'],
        filter: {
          parameter: 'expansionMicType',
          filter: (expansionMicType, items) => {
            if (expansionMicType === 'ceiling' || expansionMicType === 'table') {
              return items.filter(item => item !== 'Parlé CBC 2500 CM' && item !== 'Parlé CBC 2500 PM');
            }
            return items;
          }
        },
        dispatcher: 'setMicrophones'
      },
      {
        // PIG-420 / PIG-294: BMA360D is treated as a single user-facing model. The
        // 24" vs 600mm variant is resolved automatically by the middleware (PIG-402's
        // preselectBma360dModel) based on ceiling tile size + unit system, and the
        // actual SKU ("BMA360D" or "BMA360D-I") only appears in the equipment list.
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            return microphones && isCeilingTileMic(microphones.model);
          }
        },
        element: 'textInput',
        label: 'Model',
        name: 'modelName',
        readOnly: true,
        value: {
          parameter: 'microphones.model',
          getValue: () => 'BMA360D',
        },
        dispatcher: 'setMicrophones'
      },
      {
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            return microphones && !isBarMic(microphones.model) && !isCeilingTileMic(microphones.model);
          }
        },
        element: 'textInput',
        label: 'Model',
        name: 'modelName',
        readOnly: true,
        value: {
          parameter: 'microphones.model',
          getValue: (microphoneType) => {
            return constants.equipment.microphones[microphoneType]?.modelName || 'Parlé TCM-X';
          }
        },
        dispatcher: 'setMicrophones'
      },
      {
        condition: {
          parameter: ['microphones', 'table'],
          check: ([microphones, table]) => {
            // Show coverage for ceiling and pendant mics (including BMA360D / CEILING_TILE).
            // BMA360D's coverage selector drives the centered-grid sizing in
            // calculateMicPlacement: `focused` sizes the grid to the table box, `full_room`
            // sizes it to the room (PIG-295 AC7, PIG-445).
            // Table mics have fixed focused coverage (on table only).
            // CBC bar mics have a fixed coverage pattern with no user option (PIG-392).
            // Other bar mics don't expose coverage.
            // Hide coverage buttons when there's no table — `focused` has no meaning without
            // a table to focus on, so coverage defaults to full_room in that case.
            // Also hide when `hideCoverageToggle` is set — a manual move / add / delete
            // invalidated the focused/full_room preset (see clearPrimaryCoverage in
            // room.ts); the toggle reappears once undo clears the flag. Note this flag is
            // separate from `microphones.coverage` itself, which is left untouched by
            // manual edits so placement calculations always see a valid value.
            const model = microphones && microphones.model;
            const hasTable = Boolean(table && table.shape);
            return model && (
              (CEILING_MOUNTED.has(model) && !CBC.has(model)) ||
              PENDANT.has(model)
            ) && hasTable && !microphones.hideCoverageToggle;
          }
        },
        element: 'toggleSelect',
        label: 'Coverage',
        items: ['focused', 'full_room'],
        labels: true,
        theme: 'speakerCoverageNavCustom',
        activeState: 'speaker_coverage_selected',
        inactiveState: 'speaker_coverage_inactive',
        name: 'coverage',
        dispatcher: 'setMicrophones',
        bubbles: {
          focused: 'Focused',
          full_room: 'Full'
        },
        extend: true
      },
      {
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            const model = microphones?.model;
            return model && ((CEILING_MOUNTED.has(model) && !CBC.has(model)) || PENDANT.has(model));
          }
        },
        element: 'dimensionInput',
        label: 'Height',
        ids: ['height'],
        dispatcher: 'setMicrophones',
        errorCode: 1110
      },
      {
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => CBC.has(microphones?.model)
        },
        element: 'dimensionInput',
        label: 'Bar mounting height',
        name: 'height',
        ids: ['height'],
        dispatcher: 'setMicrophones',
        errorCode: 1110
      },
      {
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => CBC.has(microphones?.model)
        },
        element: 'angleInput',
        label: 'Horizontal angle',
        ids: ['horizontalAngle'],
        dispatcher: 'setMicrophones',
        min: 0,
        max: 359
      },
      {
        condition: {
          parameter: ['microphones', 'table'],
          check: ([microphones, table]) => {
            // Show coverage density slider for ceiling, pendant, and table microphones when:
            // 1. Table exists and has a shape
            // 2. Microphone model is one of the supported types (ceiling, pendant, or table)
            // 3. There are multiple valid jump points calculated
            // 4. The Coverage (Focus/Full) toggle isn't hidden — a manual move / add /
            //    delete invalidates both presets together (see clearPrimaryCoverage in
            //    room.ts), so the density slider hides alongside the toggle and both
            //    reappear together once undo clears the flag.
            const hasTable = Boolean(table && table.shape);
            const isSupportedMicType = !isBarMic(microphones?.model);
            const jumpPoints = microphones?.coverageDensity?.jumpPoints;
            const hasMultipleOptions = Array.isArray(jumpPoints) && jumpPoints.length >= 2;

            return hasTable && isSupportedMicType && hasMultipleOptions && !microphones?.hideCoverageToggle;
          }
        },
        element: 'fader',
        label: 'Coverage density',
        name: 'coverageDensity',
        range: 'jumpPoints',
        dispatcher: 'setMicrophones'
      },
      // Expansion mic type selector — shown when the auto pre-calc recommended expansion mics
      // (`expansionMicRecommended`) AND the user hasn't dismissed the dialog with "No thanks"
      // (`expansionMicType !== 'none'`), OR when the user has already chosen a type via the
      // dialog/menu (`expansionMicType` is 'ceiling'/'table'). Hiding on 'none' means a user
      // who declined the recommendation doesn't keep seeing the selector — they can re-engage
      // anytime via the "Add expansion microphones" button further down the menu.
      //
      // `expansionMicSuppressed` hides it too: the placement ran and declined because the gap is
      // below the minimum worth a mic, so there is no expansion mic to configure and the button at
      // the bottom of this menu has flipped back to "Add". The whole expansion group appears and
      // disappears together — see the matching guard on the model, coverage, height and density
      // controls below.
      {
        condition: {
          parameter: ['expansionMicRecommended', 'microphones', 'expansionMicType', 'expansionMicSuppressed'],
          check: ([expansionMicRecommended, microphones, expansionMicType, expansionMicSuppressed]) => {
            const model = microphones?.model;
            if (!model || !isBarMic(model) || CBC.has(model)) return false;
            if (expansionMicSuppressed) return false;
            const userChose = expansionMicType === 'ceiling' || expansionMicType === 'table';
            const userDeclined = expansionMicType === 'none';
            return userChose || (Boolean(expansionMicRecommended) && !userDeclined);
          }
        },
        element: 'selector',
        label: 'Expansion microphone type',
        items: ['ceiling', 'table', 'none'],
        filter: {
          parameter: ['table', 'platform', 'ucxMicTypeSwitchAllowed', 'expansionMicType'],
          filter: ([table, platform, ucxMicTypeSwitchAllowed, expansionMicType], items) => {
            let result = !table || !table.shape
              ? items.filter(item => item !== 'table')
              : items;

            // On a viable/fallen-back Devio UCX Android design, switching the
            // ceiling<->table expansion type is only allowed when the OTHER (candidate) type
            // would still be within Devio UCX capacity — area.ucxMicTypeSwitchAllowed
            // (deliberately no leading underscore: this value is read through
            // getStoreProp/getCurrentValue2, which runs the parameter name through lodash's
            // camelCase() — camelCase('_ucxMicTypeSwitchAllowed') strips the leading underscore
            // and silently looks up a field that doesn't exist, always resolving to null/false.
            // Naming it without the underscore up front avoids that lookup mismatch entirely),
            // computed by computeMicTypeSwitchFlag in reduxModules/ducks/room.ts. Off Android
            // this constraint doesn't apply at all (per that function's own doc comment), so
            // it's gated on platform here too, not just the flag's value, since the flag is
            // also false (for an unrelated, "not applicable" reason) for every non-Android
            // design — filtering on the flag alone would wrongly restrict Windows/Tesira
            // bar + expansion designs that were never subject to this capacity rule.
            //
            // Hiding the candidate outright (vs. showing it disabled, or allowing the pick with
            // a warning toast) is this codebase's own safeguard on top of the real capacity math
            // — not a rule from any written spec. See computeMicTypeSwitchFlag's doc comment.
            if (platform === 'Android' && !ucxMicTypeSwitchAllowed &&
                (expansionMicType === 'ceiling' || expansionMicType === 'table')) {
              const candidate = expansionMicType === 'ceiling' ? 'table' : 'ceiling';
              result = result.filter(item => item !== candidate);
            }

            return result;
          }
        },
        dispatcher: 'setExpansionMicType',
        bubbles: {
          ceiling: 'Ceiling',
          table: 'Table',
          none: 'None'
        }
      },
      // Coverage Density fader for the manual-add table expansion flow. The primary mic
      // is a (non-CBC) bar, so the slider above is hidden — but the user explicitly opted
      // into table expansion mics and they should be able to tune the count, like regular
      // table mics. Dispatcher targets `setExpansionMicrophones` so the value lives on
      // the expansion field, not the bar primary. JumpPoints are populated by the
      // middleware's table-expansion strategy when `expansionMicManualOptIn === true`.
      {
        condition: {
          parameter: ['microphones', 'expansionMicType', 'expansionMicManualOptIn', 'expansionMicrophones', 'expansionMicSuppressed'],
          check: ([microphones, expansionMicType, expansionMicManualOptIn, expansionMicrophones, expansionMicSuppressed]) => {
            const model = microphones?.model;
            if (!model || !isBarMic(model) || CBC.has(model)) return false;
            // Stale jumpPoints from an earlier placement outlive the mics, so without this the
            // fader would offer to tune the count of a layout that is currently empty.
            if (expansionMicSuppressed) return false;
            if (expansionMicType !== 'table' || !expansionMicManualOptIn) return false;
            const jumpPoints = expansionMicrophones?.coverageDensity?.jumpPoints;
            return Array.isArray(jumpPoints) && jumpPoints.length >= 2;
          }
        },
        element: 'fader',
        label: 'Coverage density',
        name: 'coverageDensity',
        range: 'jumpPoints',
        dispatcher: 'setExpansionMicrophones'
      },
      // Expansion mic model (display-only — shows the model name of the active expansion mic).
      // The textInput renderer (EntryInput.tsx) ignores `value` and resolves its display value via
      // `dispatcher` → getStoreProp() (strips "set", camelCases) → state field, then drills via `name`.
      // So `dispatcher: 'setExpansionMicrophones'` reads `areaState.expansionMicrophones` and
      // `name: 'modelName'` picks the modelName out of it. The dispatcher name doesn't correspond
      // to a real reducer, but readOnly prevents user interaction from triggering a dispatch.
      //
      // Shown whenever the user has an active expansion type ('ceiling' or 'table'), regardless of
      // whether the auto pre-calc recommended expansion — the model name is meaningful as long as
      // mics are placed.
      {
        condition: {
          parameter: ['microphones', 'expansionMicType', 'expansionMicSuppressed'],
          check: ([microphones, expansionMicType, expansionMicSuppressed]) => {
            const model = microphones?.model;
            if (expansionMicSuppressed) return false;
            return Boolean(model) && isBarMic(model) && !CBC.has(model)
              && (expansionMicType === 'ceiling' || expansionMicType === 'table');
          }
        },
        element: 'textInput',
        label: 'Expansion microphone model',
        name: 'modelName',
        readOnly: true,
        dispatcher: 'setExpansionMicrophones'
      },
      // Expansion ceiling mic coverage toggle (focused vs full_room).
      // The toggle has THREE gates, all required:
      //   1. `expansionMicType === 'ceiling'` — table expansion uses density, not focused/full
      //   2. `Boolean(table?.shape)` — preset coverage only makes sense around a table;
      //      in a no-table room the toggle has never been shown
      //   3. `expansionMicCoverage !== null` — added for PIG-307. addExpansionMic /
      //      non-last removeExpansionMic clear coverage to null when the user customises,
      //      so the toggle hides in that case (the custom layout matches neither preset).
      // Net effect of the new gate: in a with-table ceiling-expansion room, the toggle
      // hides after the user adds/deletes a mic. In a no-table room there's no UI change
      // because gate 2 already kept the toggle hidden.
      // The manual opt-in path uses `{focused: false, full_room: false}` (not null) so
      // the toggle stays visible with neither button highlighted — only the
      // customisation paths clear it to null.
      {
        condition: {
          parameter: ['expansionMicType', 'table', 'expansionMicCoverage', 'expansionMicSuppressed'],
          check: ([expansionMicType, table, expansionMicCoverage, expansionMicSuppressed]) => {
            if (expansionMicSuppressed) return false;
            return expansionMicType === 'ceiling'
              && Boolean(table?.shape)
              && expansionMicCoverage !== null;
          }
        },
        element: 'toggleSelect',
        label: 'Expansion coverage',
        items: ['focused', 'full_room'],
        labels: true,
        theme: 'speakerCoverageNavCustom',
        activeState: 'speaker_coverage_selected',
        inactiveState: 'speaker_coverage_inactive',
        dispatcher: 'setExpansionMicCoverage',
        bubbles: {
          focused: 'Focused',
          full_room: 'Full'
        },
        extend: true
      },
      // Expansion ceiling mic height input — same `dimensionInput` component as the primary
      // ceiling mic's Height field above (ids: ['height'], errorCode: 1110), but dispatching
      // to `setExpansionMicrophones` so the value lands on `area.expansionMicrophones.height`
      // instead of the primary mic. Defaults to flush ceiling height when the expansion ceiling
      // mic is first created (see setExpansionMicType / placeExpansionMicAtLocation in room.ts —
      // NOT the primary mic's height, since the primary here is always a non-CBC bar mounted at
      // table/wall height) and is independently editable afterward. Table expansion mics (ttm_x)
      // don't expose a height field, same as the primary table mic.
      {
        condition: {
          parameter: ['expansionMicType', 'expansionMicSuppressed'],
          check: ([expansionMicType, expansionMicSuppressed]) =>
            expansionMicType === 'ceiling' && !expansionMicSuppressed
        },
        element: 'dimensionInput',
        label: 'Expansion mic height',
        ids: ['height'],
        dispatcher: 'setExpansionMicrophones',
        errorCode: 1110
      },
      // "Add expansion mics" / "Remove expansion microphones" toggle button — re-opens
      // the recommendation dialog so the user can request expansion mics at any time
      // after picking a non-CBC bar mic. Placed at the bottom of the microphones menu
      // so it doesn't compete with primary mic config above. Mirrored by the speakers
      // menu's Add expansion speakers button at the bottom of that section.
      {
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            const model = microphones?.model;
            return Boolean(model) && isBarMic(model) && !CBC.has(model);
          }
        },
        element: 'expansionAddButton',
        device: 'microphones',
      },
    ],
  },
  speakers: {
    area: [
      {
        // Speaker mounting type selector (only show when not using Bar microphone)
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            return microphones && !isBarMic(microphones.model);
          }
        },
        element: 'speakerMountingSelector',
        label: 'Type',
        dispatcher: 'setSpeakerMountingType'
      },
      {
        // Show speaker model selector when not using Bar microphone
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            return microphones && !isBarMic(microphones.model);
          }
        },
        element: 'speakerModelSelector',
        label: 'Model',
        dispatcher: 'setSpeakerModel'
      },
      {
        // Show text input for Bar speaker when using Bar microphone
        condition: {
          parameter: ['microphones'],
          check: () => {
            return false;
          }
        },
        element: 'textInput',
        label: 'Speaker',
        name: 'model',
        readOnly: true,
        value: {
          parameter: 'speakers.model',
          getValue: () => 'Bar (Auto-selected)'
        },
        dispatcher: 'setSpeakers'
      },
      // Expansion speaker type selector — visible only AFTER the user has accepted the
      // recommendation dialog (`expansionSpeakerType === 'ceiling'`). The dialog itself
      // surfaces the recommendation; once the user picks "Add", this dropdown lets them
      // remove the expansion speakers via None. Pendant is not offered: bar + expansion
      // designs only support the C-IC6 family (Tree Frog port accounting spec §1). A
      // legacy saved design with 'pendant' still shows the selector so it can be changed.
      // CBC bars are excluded — they don't support expansion speakers.
      {
        condition: {
          parameter: ['microphones', 'expansionSpeakerType'],
          check: ([microphones, expansionSpeakerType]) => {
            const model = microphones?.model;
            if (!model || !isBarMic(model) || CBC.has(model)) return false;
            return expansionSpeakerType === 'ceiling' || expansionSpeakerType === 'pendant';
          }
        },
        element: 'selector',
        label: 'Expansion speaker type',
        items: ['ceiling', 'pendant', 'none'],
        // 'pendant' is listed only so a legacy saved design that already has pendant
        // expansion speakers shows its current value (instead of a blank, out-of-range
        // select); it is filtered out for everything else, and picking it resolves to
        // ceiling anyway (setExpansionSpeakerType).
        filter: {
          parameter: ['expansionSpeakerType'],
          filter: ([expansionSpeakerType], items) =>
            expansionSpeakerType === 'pendant' ? items : items.filter(item => item !== 'pendant'),
        },
        dispatcher: 'setExpansionSpeakerType',
        bubbles: {
          ceiling: 'Ceiling',
          pendant: 'Pendant',
          none: 'None'
        }
      },
      // Expansion speaker model — fully interactive dropdown listing the speaker models
      // available for the chosen expansion type (ceiling or pendant). Filters the
      // speaker equipment by mounting type, the same way the main speaker model
      // selector does for standalone configs. Selection updates
      // `area.expansionSpeakers.model` + `modelName` via `setExpansionSpeakerModel`.
      {
        condition: {
          parameter: ['microphones', 'expansionSpeakerType'],
          check: ([microphones, expansionSpeakerType]) => {
            const model = microphones?.model;
            return Boolean(model) && isBarMic(model) && !CBC.has(model)
              && (expansionSpeakerType === 'ceiling' || expansionSpeakerType === 'pendant');
          }
        },
        element: 'expansionSpeakerModelSelector',
        label: 'Expansion speaker model'
      },
      // Expansion speaker coverage toggle (focused vs full_room). Visibility matches the
      // standalone speakers Coverage toggle below — no table requirement (the focused box still
      // has meaning without a table; it shrinks around the room's reserves). Two gates:
      //   1. `expansionSpeakerType` is ceiling OR pendant — both drive focused/full_room placement
      //      (PIG-590; the placement reads expansionSpeakerCoverage regardless of mount type)
      //   2. `expansionSpeakerCoverage !== null` — null signals the user has customised
      //      the layout (addExpansionSpeaker / removeExpansionSpeaker), so neither
      //      preset matches and the toggle hides
      {
        condition: {
          parameter: ['expansionSpeakerType', 'expansionSpeakerCoverage'],
          check: ([expansionSpeakerType, expansionSpeakerCoverage]) => {
            return (expansionSpeakerType === 'ceiling' || expansionSpeakerType === 'pendant')
              && expansionSpeakerCoverage !== null;
          }
        },
        element: 'toggleSelect',
        label: 'Coverage',
        items: ['focused', 'full_room'],
        labels: true,
        theme: 'speakerCoverageNavCustom',
        activeState: 'speaker_coverage_selected',
        inactiveState: 'speaker_coverage_inactive',
        dispatcher: 'setExpansionSpeakerCoverage',
        bubbles: {
          focused: 'Focused',
          full_room: 'Full'
        },
        extend: true
      },
      {
        // Expansion pendant-speaker height input (mirrors the primary pendant/wall Height
        // field below). Pendant speakers hang below the ceiling, so they expose an editable
        // mounting height (default 8'). Bound to `area.expansionSpeakers.height` via the
        // setExpansionSpeakers dispatcher (getStoreProp maps it to that slot).
        condition: {
          parameter: ['expansionSpeakerType'],
          check: ([expansionSpeakerType]) => expansionSpeakerType === 'pendant'
        },
        element: 'dimensionInput',
        label: 'Height',
        ids: ['height'],
        dispatcher: 'setExpansionSpeakers',
      },
      {
        // Coverage options - only for ceiling speakers (not for wall speakers or bar speakers).
        // Hidden when `hideCoverageToggle` is set by a manual move/add/delete (see
        // clearPrimaryCoverage in room.ts) — reappears once undo clears the flag.
        // `speakers.coverage` itself is left untouched so placement calculations
        // (roomCalculations.ts, geometric.ts) always see a valid value.
        condition: {
          parameter: ['speakers', 'microphones'],
          check: ([speakers, microphones]) => (speakers?.mountingType === 'ceiling' || speakers?.mountingType === 'pendant')
            && !isBarMic(microphones?.model)
            && !speakers?.hideCoverageToggle
        },
        element: 'toggleSelect',
        label: 'Coverage',
        items: ['focused', 'full_room'],
        labels: true,
        theme: 'speakerCoverageNavCustom',
        activeState: 'speaker_coverage_selected',
        inactiveState: 'speaker_coverage_inactive',
        name: 'coverage',
        dispatcher: 'setSpeakers',
        bubbles: {
          focused: 'Focused',
          full_room: 'Full'
        },
        extend: true
      },
      {
        // Speaker height input - only for pendant and wall speakers
        condition: {
          parameter: ['speakers'],
          check: ([speakers]) => speakers?.mountingType === 'pendant' || speakers?.mountingType === 'wall'
        },
        element: 'dimensionInput',
        label: 'Height',
        ids: ['height'],
        dispatcher: 'setSpeakers',
        errorCode: 1111
      },
      {
        // Speaker link toggle - only for wall speakers with 2 speakers
        condition: {
          parameter: ['speakers'],
          check: ([speakers]) => speakers?.mountingType === 'wall' && speakers?.layout?.length === 2
        },
        element: 'speakerLinkToggle',
        label: 'Speaker wall placement',
        dispatcher: 'toggleSpeakerLink'
      },
      {
        // Plot type selector for visualization
        element: 'plotTypeSelector',
        label: 'Speaker plot',
        dispatcher: 'setShowMap'
      },
      // "Add expansion speakers" / "Remove expansion speakers" toggle button — placed
      // at the bottom of the speakers menu to mirror the mic side. Visible when the
      // user has a non-CBC bar mic. Toggles between Add (opens `expansionSpeakerAdd`
      // modal) and Remove (clears via `setExpansionSpeakerType('none')`).
      {
        condition: {
          parameter: ['microphones'],
          check: ([microphones]) => {
            const model = microphones?.model;
            return Boolean(model) && isBarMic(model) && !CBC.has(model);
          }
        },
        element: 'expansionAddButton',
        device: 'speakers',
      },
    ],
  },
  camera: {
    area: [
      // Camera selector for non-bar microphones and ABC 2500a
      {
        condition: { 
          parameter: ['microphones'], 
          check: ([mics]) => {
            // Show camera selector for:
            // 1. When no microphones selected
            // 2. Non-bar microphones (ceiling, pendant, tabletop)
            // 3. ABC 2500a audio bar (which needs external camera)
            // Hide for VBC 2500a video bar (which has integrated camera)
            
            if (!mics) return true;
            
            // For non-bar microphones, always show camera selector
            if (!isBarMic(mics.model)) return true;

            // For bar microphones: audio bars need external camera, video bars have integrated camera
            return !hasCamera(mics.model, mics.modelName);
          }
        },
        element: 'selector',
        label: 'Model',
        name: 'model',
        // Show none, Vidi 250, and Vidi 280
        items: ['none', 'vidi250', 'vidi280'],
        // Default to vidi280 unless using videobar
        defaultValue: {
          parameter: 'microphones',
          getValue: (mics) => {
            // No default if using VBC videobar (it has integrated camera)
            if (mics && HAS_CAMERA.has(mics.model)) {
              return 'none';
            }
            // Default to vidi280 for all other cases (including ABC audio bar)
            return 'vidi280';
          }
        },
        dispatcher: 'setCameras'
      },
      {
        condition: {
          parameter: ['cameras'],
          check: ([cameras]) => {
            // Show plot visibility toggle only for Vidi 280 (dual-imager)
            return cameras && cameras.model === 'vidi280';
          }
        },
        element: 'toggleSelect',
        label: 'Camera coverage',
        items: ['wide_only', 'zoom_only'],
        labels: true,
        theme: 'speakerCoverageNavCustom',
        activeState: 'speaker_coverage_selected',
        inactiveState: 'speaker_coverage_inactive',
        name: 'plotVisibility',
        dispatcher: 'setCameras',
        bubbles: {
          wide_only: 'Wide',
          zoom_only: 'Zoom'
        },
        extend: true
      },
    ],
  },
  equipment_list: {
    // WDT-90 field-order fix: Platform → Product Family → UC Compute.
    // Platform is what constrains valid product families for the room; UC
    // Compute (MTR/Zoom/None) is an additive choice layered on top of an
    // already-resolved Platform/Family, not a prerequisite for either — so
    // none of the three fields below gates another's visibility. Leaving UC
    // Compute at its default ('None') no longer hides Platform or Product
    // Family; see Tree Frog UC Platform & Equipment Selection Logic §2.2/§3.4.
    area: [
      // Platform (Windows/Android) is always visible — no longer gated on a
      // UC Compute selection. Bar mic + expansion mics/speakers makes Devio
      // UCX the only valid platform (see isDevioUcxEligible's `exclusive`
      // flag in toolConstants/conferenceroomDesigner/treeFrogPorts.js); in
      // that case the control stays visible (shows Android) but disabled —
      // RadioButtons.tsx reads that same eligibility to lock it, rather than
      // hiding the choice entirely.
      {
        element: 'radioButtons',
        label: 'Platform',
        name: 'platform',
        items: ['Windows', 'Android'],
        dispatcher: 'setPlatform',
        isPlatform: true,
      },
      {
        // Product Family (Devio/Tesira) is now ALWAYS visible on the
        // Equipment List summary sub-tab — no longer hidden for bar systems,
        // oversized layouts, or Platform = Android. Hiding it based on those
        // other fields violated the core WDT-90 rule ("all three fields
        // always visible; no field's visibility gated by another field's
        // selection") — reported as a real defect where Bar + expansion
        // mic/speaker made the control disappear entirely instead of showing
        // the auto-resolved family, disabled. The `isProductFamily` flag lets
        // Selector.tsx reproduce the exact same auto-resolve rules that used
        // to gate visibility here (hasBar, oversized layout, Android
        // exclusive/port-overflow) by disabling the control instead of
        // hiding it — see the `familyLocked` comment in Selector.tsx.
        condition: { parameter: ['showTable'], check: ([table]) => table === 'summary' },
        element: 'selector',
        placeHolder: 'Select DSP Family',
        // Values are the internal family keys (must match equipmentCatalog `productFamily` and the
        // `isTreeFrog === 'Devio'` checks). `bubbles` only changes the DISPLAY label, so the Devio
        // option shows as "Devio SCX" while still dispatching 'Devio' — otherwise getProcessors
        // can't match a Devio DSP and falls back to a TesiraFORTÉ (PIG-600).
        items: ['Devio', 'Tesira'],
        bubbles: { Devio: 'Devio SCX', Tesira: 'Tesira' },
        label: 'Product Family',
        dispatcher: 'setProductFamily',
        isProductFamily: true,
      },
      // UC Compute is last and always visible — an additive choice that only
      // resolves the specific hardware SKU on Windows (Lenovo-MTR vs
      // Lenovo-Zoom, see logic.js) and has no equipment impact on Android
      // (the same UCX device serves both platforms). Changing it never
      // alters Platform or Family.
      {
        element: 'selector',
        placeHolder: 'Select UC Compute',
        items: ['None', 'Microsoft Teams', 'Zoom Rooms'],
        // Zoom Rooms has no Devio UCX compute SKU, so it is removed from the
        // list while the design resolves to Devio UCX (Devio family + Android,
        // i.e. isDevioUcxDesign) and restored as soon as it doesn't — Devio SCX
        // / Tesira / Parlé (Windows) designs keep the full three options.
        // Reducer-side enforcement (a design that BECOMES Devio UCX with Zoom
        // Rooms already stored) lives in normalizeUcComputeForUcx —
        // utilities/treeFrogBundleMatching.js.
        filter: {
          parameter: ['productFamily', 'platform'],
          filter: ([productFamily, platform], items) =>
            isDevioUcxDesign({ productFamily, platform })
              ? items.filter(item => item !== UCX_UNSUPPORTED_UC_COMPUTE)
              : items,
        },
        label: 'UC Compute',
        dispatcher: 'setUCCompute',
      },
    ],
  }

}

export default menus;
